import { computed, ref } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { isAppearancePresetId, type AppearancePresetId } from '@/utils/appearanceTheme'
import {
  normalizarKilosPorBulto,
  normalizarModulos,
  numeroPositivoONull,
  resolverModuloPorDefecto,
  type ModuloId,
} from '@/utils/modulos'
import { normalizarModoDivision, type ModoDivision } from '@/utils/division-galpon'

type ClienteOption = {
  value: number
  label: string
  appearancePreset?: AppearancePresetId | null
  powerBiUrl?: string | null
  modulos: ModuloId[]
  moduloPorDefecto: ModuloId
  kilosPorBulto: number
  modoDivision: ModoDivision
  pesoReferenciaConversion: number | null
  ajusteConversionPorKg: number | null
}

const STORAGE_KEY = 'superadmin_selected_cliente_id'

const clienteOptionsState = ref<ClienteOption[]>([])
const selectedClienteIdState = ref<number | null>(null)
const loadingState = ref(false)

const savedClienteId = Number(localStorage.getItem(STORAGE_KEY))
if (Number.isFinite(savedClienteId) && savedClienteId > 0) {
  selectedClienteIdState.value = savedClienteId
}

export function useSuperadminCliente() {
  const auth = useAuthStore()
  const isSuperadmin = computed(() => auth.user?.role === 'superadmin')

  const selectedClienteId = computed(() => selectedClienteIdState.value)
  const clienteOptions = computed(() => clienteOptionsState.value)
  const loadingClientes = computed(() => loadingState.value)

  const selectedCliente = computed(() => {
    const selectedId = selectedClienteIdState.value
    if (!selectedId) return null
    return clienteOptionsState.value.find((cliente) => cliente.value === selectedId) ?? null
  })

  const selectedClienteLabel = computed(() => selectedCliente.value?.label ?? '')
  const selectedClienteAppearancePreset = computed(
    () => selectedCliente.value?.appearancePreset ?? null
  )
  const selectedClientePowerBiUrl = computed(() => selectedCliente.value?.powerBiUrl ?? null)

  const setSelectedClienteId = (value: number | null | undefined) => {
    const parsed = Number(value)
    selectedClienteIdState.value = Number.isFinite(parsed) && parsed > 0 ? parsed : null

    if (selectedClienteIdState.value) {
      localStorage.setItem(STORAGE_KEY, String(selectedClienteIdState.value))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  const fetchClientes = async () => {
    if (!isSuperadmin.value) return

    loadingState.value = true
    try {
      const res = await api.get('/api/clientes')
      const data = (res.data?.data ?? []) as Array<any>
      clienteOptionsState.value = data
        .map((item) => {
          const attributes = item?.attributes ?? item
          const nombre = attributes?.NombreCliente
          const appearancePreset = attributes?.appearancePreset ?? null
          const powerBiUrl = attributes?.PowerBiUrl
          const modulos = normalizarModulos(attributes?.modulos)

          return {
            value: Number(item?.id),
            label: typeof nombre === 'string' ? nombre : '',
            appearancePreset:
              typeof appearancePreset === 'string' && isAppearancePresetId(appearancePreset)
                ? appearancePreset
                : null,
            powerBiUrl: typeof powerBiUrl === 'string' && powerBiUrl.trim() ? powerBiUrl.trim() : null,
            modulos,
            moduloPorDefecto: resolverModuloPorDefecto(modulos, attributes?.moduloPorDefecto),
            kilosPorBulto: normalizarKilosPorBulto(attributes?.KilosPorBultoAlimento),
            modoDivision: normalizarModoDivision(attributes?.ModoDivisionGalpon),
            pesoReferenciaConversion: numeroPositivoONull(attributes?.PesoReferenciaConversionKg),
            ajusteConversionPorKg: numeroPositivoONull(attributes?.AjusteConversionPorKg),
          } satisfies ClienteOption
        })
        .filter((item) => Number.isFinite(item.value) && item.value > 0 && item.label.length > 0)

      if (
        !selectedClienteIdState.value ||
        !clienteOptionsState.value.some((c) => c.value === selectedClienteIdState.value)
      ) {
        setSelectedClienteId(clienteOptionsState.value[0]?.value ?? null)
      }
    } finally {
      loadingState.value = false
    }
  }

  return {
    isSuperadmin,
    clienteOptions,
    selectedCliente,
    selectedClienteId,
    selectedClienteLabel,
    selectedClienteAppearancePreset,
    selectedClientePowerBiUrl,
    loadingClientes,
    setSelectedClienteId,
    fetchClientes,
  }
}
